import { useEffect, useState } from "react";
import { Plus, Phone, MessageCircle, Mail, Calendar, FileText, Send } from "lucide-react";
import api from "../lib/api";
import Modal from "./Modal";
import SearchableSelect from "./SearchableSelect";
import VoiceTextarea from "./VoiceTextarea";

const TYPES = [
  { v: "call", label: "Call", icon: Phone },
  { v: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { v: "sms", label: "SMS", icon: Send },
  { v: "email", label: "Email", icon: Mail },
  { v: "meeting", label: "Meeting", icon: Calendar },
  { v: "note", label: "Note", icon: FileText },
];

const empty = {
  type: "call",
  direction: "out",
  notes: "",
  duration_min: "",
  contact_id: "",
  linked_lead_id: "",
  linked_jobcard_id: "",
  linked_ticket_id: "",
};

export default function CallLog({ customerId, leads = [], jobcards = [], tickets = [], contacts = [] }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get(`/calls?customer_id=${customerId}`);
    setList(data);
  };
  useEffect(() => {
    if (customerId) load();
  }, [customerId]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      customer_id: customerId,
      type: form.type,
      direction: form.direction,
      notes: form.notes,
      duration_min: form.duration_min ? Number(form.duration_min) : null,
      contact_id: form.contact_id || null,
      linked_lead_id: form.linked_lead_id || null,
      linked_jobcard_id: form.linked_jobcard_id || null,
      linked_ticket_id: form.linked_ticket_id || null,
    };
    await api.post("/calls", payload);
    setOpen(false);
    setForm(empty);
    load();
  };

  const linkLabel = (call) => {
    if (call.linked_lead_id) {
      const l = leads.find((x) => x.id === call.linked_lead_id);
      return l ? `Lead · ${l.title}` : null;
    }
    if (call.linked_jobcard_id) {
      const j = jobcards.find((x) => x.id === call.linked_jobcard_id);
      return j ? `Job · ${j.title}` : null;
    }
    if (call.linked_ticket_id) {
      const t = tickets.find((x) => x.id === call.linked_ticket_id);
      return t ? `Ticket · ${t.subject}` : null;
    }
    return null;
  };

  return (
    <section className="hv-card p-5" data-testid="calllog-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-xl font-bold">Activity / Call log</h3>
        <button onClick={() => setOpen(true)} className="hv-btn hv-btn-primary" data-testid="call-new-btn">
          <Plus className="w-4 h-4" /> Log activity
        </button>
      </div>

      <div className="space-y-3">
        {list.length === 0 && <div className="text-sm text-[#647087]">No activity yet.</div>}
        {list.map((c) => {
          const t = TYPES.find((x) => x.v === c.type) || TYPES[0];
          const link = linkLabel(c);
          return (
            <div key={c.id} className="flex gap-3" data-testid={`call-${c.id}`}>
              <div
                className={`mt-0.5 w-8 h-8 rounded-md flex items-center justify-center border ${
                  c.direction === "in"
                    ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                    : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                }`}
              >
                <t.icon className="w-4 h-4" />
              </div>
              <div className="flex-1 border border-white/5 rounded-md p-3 bg-white/[0.02]">
                <div className="flex items-center justify-between text-xs">
                  <div className="text-[#a0aabf] uppercase tracking-wider">
                    {t.label} · {c.direction === "in" ? "Inbound" : "Outbound"}
                    {c.duration_min ? ` · ${c.duration_min}m` : ""}
                  </div>
                  <div className="text-[#647087]">{new Date(c.created_at).toLocaleString('en-GB')}</div>
                </div>
                {c.notes && <div className="text-sm mt-1.5 whitespace-pre-wrap">{c.notes}</div>}
                <div className="text-xs text-[#647087] mt-1">
                  by <span className="text-[#1fb6c1]">{c.by_user_name}</span>
                  {link && <> · linked to {link}</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Log activity" testId="call-modal">
        <form onSubmit={submit} className="space-y-3" data-testid="call-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="hv-input mt-1"
                data-testid="call-input-type"
              >
                {TYPES.map((t) => (
                  <option key={t.v} value={t.v}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Direction</label>
              <select
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}
                className="hv-input mt-1"
              >
                <option value="out">Outbound</option>
                <option value="in">Inbound</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Contact</label>
            <select
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              className="hv-input mt-1"
            >
              <option value="">— Customer —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.role ? `(${c.role})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
            <VoiceTextarea
              value={form.notes}
              onChange={(v) => setForm({...form, notes: v})}
              data-testid="call-input-notes"
              placeholder="What was discussed?"
             testIdPrefix="call-input-notes-voice" context="note" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Duration (min)</label>
              <input
                type="number"
                value={form.duration_min}
                onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                className="hv-input mt-1"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Link to lead</label>
              <SearchableSelect
                value={form.linked_lead_id || ""}
                onChange={(id) =>
                  setForm({ ...form, linked_lead_id: id, linked_jobcard_id: "", linked_ticket_id: "" })
                }
                options={leads.map((l) => ({
                  id: l.id,
                  label: l.title || "Untitled lead",
                  sublabel: [l.customer_name, l.phase].filter(Boolean).join(" · "),
                  searchTerms: [l.title, l.customer_name, l.phase, l.industry, l.contact_name].filter(Boolean),
                }))}
                placeholder="— None —"
                testId="call-input-linked-lead"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Link to job</label>
              <select
                value={form.linked_jobcard_id}
                onChange={(e) =>
                  setForm({ ...form, linked_jobcard_id: e.target.value, linked_lead_id: "", linked_ticket_id: "" })
                }
                className="hv-input mt-1"
              >
                <option value="">— None —</option>
                {jobcards.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Link to ticket</label>
              <select
                value={form.linked_ticket_id}
                onChange={(e) =>
                  setForm({ ...form, linked_ticket_id: e.target.value, linked_lead_id: "", linked_jobcard_id: "" })
                }
                className="hv-input mt-1"
              >
                <option value="">— None —</option>
                {tickets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.subject}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="hv-btn hv-btn-primary w-full" data-testid="call-submit">
            Log activity
          </button>
        </form>
      </Modal>
    </section>
  );
}
